/**
 * Best-Value Study Destinations.
 *
 * A single-page data experience ranking 22 countries by financial value
 * for an undergraduate degree. Combines:
 *   - International vs home-fee eligibility (from the fee-status engine)
 *   - Degree cost + living cost over typical degree length
 *   - Starting graduate salary → 10-yr net ROI + simple payback
 *   - Access to major scholarships
 *   - Post-study work rights & PR pathway
 *
 * Design intent:
 *   - Editorial header that explains the methodology in-line, not hidden
 *     behind a tiny tooltip. People trust ROI numbers only when they can
 *     see the formula.
 *   - Filter chips (subject + sort) are the only controls — no left-rail
 *     filter wall; the data has to earn attention on its own.
 *   - Personalisation banner at the top: if signed-in, the ranking is
 *     already using the user's fee status; if not, we show the "sign in
 *     to personalise" nudge without breaking the table for guests.
 *   - Country rows are expandable to a per-subject grid + scholarship
 *     and visa summary — detail without a page reload.
 */
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import {
  ChevronDown, ChevronRight, Sparkles, TrendingUp, Clock, Award,
  Briefcase, ShieldCheck, AlertTriangle, ExternalLink, Wallet, Pin, X, Share2, Link2, Check,
} from "lucide-react";

const SUBJECTS = [
  { key: "default",   label: "Overall" },
  { key: "business",  label: "Business & Management" },
  { key: "computing", label: "Computing & Engineering" },
  { key: "medicine",  label: "Medicine & Health" },
  { key: "arts",      label: "Arts, Humanities & Social Sciences" },
  { key: "law",       label: "Law" },
];

const SORTS = [
  { key: "value",   label: "Best overall value", Icon: Sparkles },
  { key: "roi",     label: "Highest 10-yr ROI",  Icon: TrendingUp },
  { key: "payback", label: "Fastest payback",    Icon: Clock },
  { key: "cost",    label: "Lowest total cost",  Icon: Wallet },
];

export default function ValueCountriesPage() {
  const { formatFrom }                 = useCurrency();
  // URL ↔ state. `?sub=`, `?sort=` and `?pin=GB,US,DE` round-trip the
  // user's compare state so a shared link opens the same view.
  const [search, setSearch]            = useSearchParams();
  const [subject, setSubjectState]     = useState(() => search.get("sub") || "default");
  const [sort, setSortState]           = useState(() => search.get("sort") || "value");
  const [data, setData]                = useState(null);
  const [methodology, setMethodology]  = useState(null);
  const [err, setErr]                  = useState(null);
  const [expanded, setExpanded]        = useState(null);     // code of expanded row
  const [pinned, setPinnedState]       = useState(() => {
    const raw = search.get("pin");
    return raw ? raw.split(",").filter(Boolean).slice(0, 3) : [];
  });

  // Any time one of the three URL-backed states changes, mirror it back
  // to the URL so refresh + copy-paste share works. We call setSearch
  // with `{ replace: true }` so each pin / unpin doesn't pollute the
  // browser history.
  const syncUrl = (next) => {
    const np = new URLSearchParams(search);
    const { subject: s, sort: so, pinned: p } = { subject, sort, pinned, ...next };
    if (s && s !== "default") np.set("sub", s); else np.delete("sub");
    if (so && so !== "value") np.set("sort", so); else np.delete("sort");
    if (p && p.length) np.set("pin", p.join(",")); else np.delete("pin");
    setSearch(np, { replace: true });
  };
  const setSubject = (v) => { setSubjectState(v); syncUrl({ subject: v }); };
  const setSort    = (v) => { setSortState(v);    syncUrl({ sort: v }); };
  const setPinned  = (v) => {
    // Accept both raw array and updater fn so the togglePin call-site
    // signature doesn't have to change.
    setPinnedState((prev) => {
      const next = typeof v === "function" ? v(prev) : v;
      syncUrl({ pinned: next });
      return next;
    });
  };

  // Fetch league table whenever subject or sort changes.
  useEffect(() => {
    setData(null);
    const params = new URLSearchParams();
    if (subject && subject !== "default") params.set("subject", subject);
    params.set("sort", sort);
    api.get(`/value/countries?${params.toString()}`)
      .then(r => setData(r.data))
      .catch(e => setErr(e?.message || "Failed to load."));
  }, [subject, sort]);

  // Methodology is static — fetch once.
  useEffect(() => {
    api.get("/value/methodology").then(r => setMethodology(r.data)).catch(() => {});
  }, []);

  const rows = data?.rows || [];
  const personalised = data?.personalised;

  // Pin helpers — cap at 3 to keep the comparison panel readable on
  // mobile. Pinning a 4th replaces the oldest. The toggle is on the
  // row header so the user doesn't have to expand the row to pin.
  const togglePin = (code) => {
    setPinned((prev) => {
      if (prev.includes(code)) return prev.filter((c) => c !== code);
      return (prev.length >= 3 ? [...prev.slice(1), code] : [...prev, code]);
    });
  };
  const pinnedRows = pinned.map((c) => rows.find((r) => r.code === c)).filter(Boolean);

  // We'll re-pick the "best value" headline from the top row for the hero
  // card so the number matches whatever sort/subject is active.
  const headline = rows[0];

  return (
    <div className="min-h-screen bg-stone-50" data-testid="value-countries-page">
      {/* OG + Twitter meta: when any country is pinned, point unfurlers
          at the dynamic share.png so a shared link previews the exact
          cards the sender saw. We fall back to a 3-country default
          (UK/US/DE) when nothing is pinned so the preview still looks
          intentional, not blank. */}
      <ValueCountriesHelmet pinned={pinned} subject={subject} />
      {/* ── Hero ─────────────────────────────────────────────── */}
      <div className="bg-gradient-to-br from-[#1E1B4B] via-[#4C1D95] to-[#5B21B6] text-white">
        <div className="max-w-6xl mx-auto px-4 pt-10 pb-12">
          <div className="text-[11px] font-underground-sc tracking-[0.18em] text-amber-200 mb-2">
            Data feature · reviewed {methodology?.last_reviewed || "quarterly"}
          </div>
          <h1 className="font-editorial text-4xl md:text-5xl leading-tight mb-3" data-testid="hero-title">
            Which country gives you the <span className="text-amber-300">best value</span> for an undergraduate degree?
          </h1>
          <p className="text-stone-200 text-[15px] max-w-3xl leading-relaxed">
            We rank 22 destinations on five financial dimensions: tuition &
            living cost, home-fee eligibility, access to major scholarships,
            post-study work rights, and starting graduate salary — then convert
            them into a 10-year ROI and a simple payback period you can read in
            seconds.
          </p>

          {/* Headline card */}
          {headline && (
            <div
              className="mt-7 rounded-2xl bg-white/5 border border-white/10 backdrop-blur px-5 py-4 flex flex-wrap gap-6 items-center"
              data-testid="hero-headline"
            >
              <div className="text-3xl">{headline.flag}</div>
              <div>
                <div className="text-[11px] uppercase tracking-wider text-amber-200/90">#1 by {SORTS.find(s => s.key === sort)?.label.toLowerCase()}</div>
                <div className="font-editorial text-2xl">{headline.name}</div>
              </div>
              <div className="h-10 w-px bg-white/15 hidden sm:block" />
              <StatBlock label="10-yr net ROI"  value={formatFrom(headline.net_roi_10yr_gbp, "GBP")} />
              <StatBlock label="Payback"        value={headline.payback_years ? `${headline.payback_years} yrs` : "—"} />
              <StatBlock label="Total degree cost" value={formatFrom(headline.total_cost_gbp, "GBP")} muted />
            </div>
          )}
        </div>
      </div>

      {/* ── Personalisation banner ────────────────────────────── */}
      <div className="max-w-6xl mx-auto px-4 mt-6">
        {personalised ? (
          <div
            className="rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-900 px-4 py-3 flex gap-3 items-start"
            data-testid="personalisation-banner-authed"
          >
            <ShieldCheck className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5" />
            <div className="text-[13px] leading-relaxed">
              <strong>Personalised</strong> — this ranking uses your profile's
              fee-status inference. Your home-fee eligibility is highlighted
              on each row.
              {data?.profile_summary?.nationalities?.length > 0 && (
                <div className="text-[11px] text-emerald-800 mt-1 font-mono">
                  nationality · {data.profile_summary.nationalities.join(", ")}
                  {data.profile_summary.residence_country && (
                    <> &nbsp;·&nbsp; residing in · {data.profile_summary.residence_country}</>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div
            className="rounded-xl border border-amber-200 bg-amber-50 text-amber-950 px-4 py-3 flex gap-3 items-start"
            data-testid="personalisation-banner-guest"
          >
            <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
            <div className="text-[13px] leading-relaxed">
              You're seeing the international-fee ranking. {" "}
              <Link to="/profile" className="underline underline-offset-2 font-semibold hover:text-amber-900" data-testid="personalise-signin-link">
                Sign in and complete your fee-status profile
              </Link>{" "}
              to see the version that uses your home-fee eligibility.
            </div>
          </div>
        )}
      </div>

      {/* ── Filter chips ──────────────────────────────────────── */}
      <div className="max-w-6xl mx-auto px-4 mt-6 space-y-3">
        <ChipRow
          label="Subject"
          testIdPrefix="subject"
          options={SUBJECTS}
          selected={subject}
          onSelect={setSubject}
        />
        <ChipRow
          label="Sort by"
          testIdPrefix="sort"
          options={SORTS.map(s => ({ key: s.key, label: s.label }))}
          selected={sort}
          onSelect={setSort}
        />
      </div>

      {/* ── League table ──────────────────────────────────────── */}
      <div className="max-w-6xl mx-auto px-4 mt-6 pb-16">
        {err && <div className="text-sm text-red-700" data-testid="load-error">{err}</div>}
        {!data && !err && <div className="text-sm text-stone-500 py-20 text-center">Loading rankings…</div>}

        {rows.length > 0 && (
          <div className="rounded-2xl border border-stone-200 bg-white overflow-hidden" data-testid="league-table">
            {/* Desktop header */}
            <div className="hidden md:grid grid-cols-[32px_28px_1.5fr_1fr_1fr_1fr_100px_28px_32px] px-4 py-2.5 bg-stone-50 border-b border-stone-200 text-[11px] uppercase tracking-wider text-stone-500 gap-2">
              <div>#</div>
              <div />
              <div>Country</div>
              <div>Total cost</div>
              <div>10-yr ROI</div>
              <div>Payback</div>
              <div className="text-right">Value</div>
              <div />
              <div />
            </div>

            {rows.map((r, i) => (
              <CountryRow
                key={r.code}
                row={r}
                rank={i + 1}
                expanded={expanded === r.code}
                onToggle={() => setExpanded(expanded === r.code ? null : r.code)}
                formatFrom={formatFrom}
                subject={subject}
                isPinned={pinned.includes(r.code)}
                onTogglePin={() => togglePin(r.code)}
              />
            ))}
          </div>
        )}

        {/* ── Comparison panel (sticky at bottom when pinned) ─ */}
        {pinnedRows.length > 0 && (
          <ComparePanel
            rows={pinnedRows}
            onUnpin={togglePin}
            onClear={() => setPinned([])}
            formatFrom={formatFrom}
          />
        )}

        {/* ── Methodology footer ─────────────────────────────── */}
        {methodology && (
          <details className="mt-8 rounded-xl border border-stone-200 bg-white p-5" data-testid="methodology-footer">
            <summary className="cursor-pointer text-[13px] font-semibold text-stone-900 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-[#4C1D95]" />
              How we compute these numbers
            </summary>
            <div className="mt-4 space-y-3 text-[13px] text-stone-700 leading-relaxed">
              {methodology.model?.map((m) => (
                <div key={m.name}>
                  <div className="font-semibold text-stone-900">{m.name}</div>
                  <div className="text-stone-600 text-[12.5px]">{m.notes}</div>
                </div>
              ))}
              <div className="pt-3 border-t border-stone-200 mt-4">
                <div className="text-[11px] uppercase tracking-wider text-stone-500 mb-2">Sources</div>
                <ul className="space-y-1 text-[12px] text-stone-600">
                  {methodology.sources && Object.entries(methodology.sources).map(([k, v]) => (
                    <li key={k}><span className="font-mono text-stone-500">{k}</span> — {v}</li>
                  ))}
                </ul>
              </div>
              <div className="text-[11.5px] text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-3">
                {methodology.disclaimer}
              </div>
            </div>
          </details>
        )}
      </div>
    </div>
  );
}

/* ── Row components ─────────────────────────────────────── */

function CountryRow({ row, rank, expanded, onToggle, formatFrom, subject, isPinned, onTogglePin }) {
  const isHome = row.fee_status?.status === "home";
  const handlePinClick = (e) => {
    e.stopPropagation();
    onTogglePin?.();
  };
  return (
    <div className="border-b border-stone-200 last:border-b-0" data-testid={`country-row-${row.code}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-4 py-3.5 hover:bg-stone-50 transition text-left"
        data-testid={`country-row-toggle-${row.code}`}
      >
        <div className="hidden md:grid grid-cols-[32px_28px_1.5fr_1fr_1fr_1fr_100px_28px_32px] gap-2 items-center">
          <div className="text-xs font-mono text-stone-400">{rank}</div>
          <div className="text-2xl leading-none">{row.flag}</div>
          <div>
            <div className="font-semibold text-stone-900 flex items-center gap-2">
              {row.name}
              {isHome && (
                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 uppercase tracking-wider" data-testid={`home-fee-badge-${row.code}`}>
                  Home fee
                </span>
              )}
            </div>
            <div className="text-[11px] text-stone-500 mt-0.5 line-clamp-1">{row.tags?.slice(0, 3).join(" · ")}</div>
          </div>
          <div className="text-[13px] text-stone-800 tabular-nums">{formatFrom(row.total_cost_gbp, "GBP")}</div>
          <div className={`text-[13px] tabular-nums ${row.net_roi_10yr_gbp >= 0 ? "text-emerald-700 font-semibold" : "text-red-700"}`}>
            {formatFrom(row.net_roi_10yr_gbp, "GBP")}
          </div>
          <div className="text-[13px] text-stone-800 tabular-nums">{row.payback_years ? `${row.payback_years} yrs` : "—"}</div>
          <div className="text-right">
            <div className="inline-block px-2.5 py-0.5 rounded-full bg-[#F5F0FF] text-[#4C1D95] text-[12px] font-semibold tabular-nums">
              {row.value_score}
            </div>
          </div>
          <div
            role="button"
            tabIndex={0}
            onClick={handlePinClick}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handlePinClick(e); }}
            data-testid={`country-pin-${row.code}`}
            aria-pressed={isPinned}
            aria-label={isPinned ? "Unpin from compare" : "Pin to compare"}
            className={`flex justify-center cursor-pointer transition-colors ${isPinned ? "text-amber-500" : "text-stone-300 hover:text-stone-500"}`}
          >
            <Pin className={`w-4 h-4 ${isPinned ? "fill-amber-400" : ""}`} />
          </div>
          <div className="text-stone-400 flex justify-end">
            {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </div>
        </div>

        {/* Mobile view */}
        <div className="md:hidden flex gap-3 items-start">
          <div className="text-xs font-mono text-stone-400 mt-1">{rank}</div>
          <div className="text-2xl leading-none mt-0.5">{row.flag}</div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-stone-900 flex items-center gap-2 flex-wrap">
              {row.name}
              {isHome && (
                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 uppercase tracking-wider">
                  Home fee
                </span>
              )}
            </div>
            <div className="text-[11px] text-stone-500 mt-0.5">{row.tags?.slice(0, 2).join(" · ")}</div>
            <div className="mt-2 flex gap-3 text-[12px]">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-stone-400">ROI</div>
                <div className={`tabular-nums ${row.net_roi_10yr_gbp >= 0 ? "text-emerald-700 font-semibold" : "text-red-700"}`}>
                  {formatFrom(row.net_roi_10yr_gbp, "GBP")}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-stone-400">Payback</div>
                <div className="text-stone-800 tabular-nums">{row.payback_years ? `${row.payback_years} yrs` : "—"}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-stone-400">Score</div>
                <div className="font-semibold text-[#4C1D95] tabular-nums">{row.value_score}</div>
              </div>
            </div>
          </div>
          <div
            role="button"
            tabIndex={0}
            onClick={handlePinClick}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handlePinClick(e); }}
            data-testid={`country-pin-mobile-${row.code}`}
            aria-pressed={isPinned}
            aria-label={isPinned ? "Unpin from compare" : "Pin to compare"}
            className={`mt-1 cursor-pointer transition-colors ${isPinned ? "text-amber-500" : "text-stone-300"}`}
          >
            <Pin className={`w-4 h-4 ${isPinned ? "fill-amber-400" : ""}`} />
          </div>
          <div className="text-stone-400 mt-1">
            {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </div>
        </div>
      </button>

      {expanded && <CountryExpand row={row} formatFrom={formatFrom} subject={subject} />}
    </div>
  );
}

function CountryExpand({ row, formatFrom, subject }) {
  const sch   = row.scholarships  || {};
  const psw   = row.post_study_work || {};
  const elig  = row.home_eligibility || {};
  return (
    <div className="px-4 py-4 bg-stone-50 border-t border-stone-200" data-testid={`country-expand-${row.code}`}>
      <div className="grid md:grid-cols-2 gap-4">
        {/* Column 1 — breakdown */}
        <div className="space-y-3">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-stone-500 mb-1 flex items-center gap-1">
              <Wallet className="w-3 h-3" /> Cost breakdown
            </div>
            <ul className="text-[13px] text-stone-700 space-y-1">
              <li><span className="font-mono text-stone-500">Tuition / yr</span> &nbsp;{formatFrom(row.annual_fee_gbp, "GBP")} <span className="text-[11px] text-stone-500">({row.fee_status?.status} rate)</span></li>
              <li><span className="font-mono text-stone-500">Living / yr</span> &nbsp;{formatFrom(row.annual_living_gbp, "GBP")}</li>
              <li><span className="font-mono text-stone-500">Degree length</span> &nbsp;{row.degree_length_yr} yrs</li>
              <li className="pt-1 border-t border-stone-200 mt-1 text-stone-900 font-semibold">
                Total cost &nbsp;{formatFrom(row.total_cost_gbp, "GBP")}
              </li>
              <li><span className="font-mono text-stone-500">Starting salary</span> &nbsp;{formatFrom(row.grad_salary_gbp, "GBP")} / yr</li>
            </ul>
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-wider text-stone-500 mb-1 flex items-center gap-1">
              <ShieldCheck className="w-3 h-3" /> Home-fee eligibility
            </div>
            {row.fee_status?.status === "home" ? (
              <div
                className="text-[12.5px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md px-2.5 py-1.5 inline-block"
                data-testid={`you-qualify-${row.code}`}
              >
                ✓ You qualify for the home rate here
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-1">
                  <div className="h-2 w-24 bg-stone-200 rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-500" style={{ width: `${(elig.ease_score || 0) * 10}%` }} />
                  </div>
                  <span className="text-[11px] font-mono text-stone-500">{elig.ease_score}/10 ease (general)</span>
                </div>
                <div className="text-[12px] text-stone-600 leading-snug">{elig.notes}</div>
              </>
            )}
            {row.fee_status?.reason && (
              <div className="text-[11.5px] text-stone-500 italic mt-1">Your inference: {row.fee_status.reason}</div>
            )}
          </div>
        </div>

        {/* Column 2 — scholarships & post-study work */}
        <div className="space-y-3">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-stone-500 mb-1 flex items-center gap-1">
              <Award className="w-3 h-3" /> Scholarships
            </div>
            <div className="text-[13px] text-stone-800">
              <span className="font-semibold">{sch.major_count}</span> major schemes · avg{" "}
              <span className="font-semibold">{sch.avg_coverage_pct}%</span> coverage
            </div>
            {sch.flagships?.length > 0 && (
              <div className="text-[12px] text-stone-600 mt-1">
                Flagship: {sch.flagships.join(", ")}
              </div>
            )}
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-wider text-stone-500 mb-1 flex items-center gap-1">
              <Briefcase className="w-3 h-3" /> Post-study work
            </div>
            <div className="text-[13px] text-stone-800">
              <span className="font-semibold">{psw.visa_name}</span> · {psw.years} yr{psw.years === 1 ? "" : "s"}
            </div>
            <div className="text-[12px] text-stone-600 mt-1">
              PR pathway ~ {psw.pr_pathway_years} yrs · ease {psw.score}/10
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            {row.fee_status?.authority_url && (
              <a
                href={row.fee_status.authority_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[12px] font-semibold text-[#4C1D95] inline-flex items-center gap-1 hover:underline"
                data-testid={`authority-link-${row.code}`}
              >
                Confirm fee rules <ExternalLink className="w-3 h-3" />
              </a>
            )}
            <Link
              to={`/explore?country=${row.code}`}
              className="text-[12px] font-semibold text-stone-700 hover:text-stone-900 inline-flex items-center gap-1 hover:underline ml-auto"
              data-testid={`explore-link-${row.code}`}
            >
              Browse universities <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Shared small bits ─────────────────────────────────── */

function ChipRow({ label, options, selected, onSelect, testIdPrefix }) {
  return (
    <div className="flex gap-2 items-center flex-wrap">
      <div className="text-[11px] uppercase tracking-wider text-stone-500 mr-1">{label}</div>
      {options.map((o) => (
        <button
          key={o.key}
          type="button"
          onClick={() => onSelect(o.key)}
          data-testid={`${testIdPrefix}-chip-${o.key}`}
          className={`text-[12px] px-3 py-1.5 rounded-full border transition ${
            selected === o.key
              ? "bg-[#4C1D95] text-white border-[#4C1D95]"
              : "bg-white text-stone-700 border-stone-200 hover:border-stone-400"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function StatBlock({ label, value, muted }) {
  return (
    <div>
      <div className={`text-[10px] uppercase tracking-wider ${muted ? "text-stone-400" : "text-amber-200/90"}`}>{label}</div>
      <div className={`text-lg font-semibold ${muted ? "text-stone-300" : "text-white"} tabular-nums`}>{value}</div>
    </div>
  );
}

/**
 * OG + Twitter meta tags. The share.png endpoint returns a 1200×630
 * image whose contents already reflect the pinned codes + subject, so
 * we just point unfurlers at the right URL. Logged-in visitors see
 * their personalised rates in the preview card itself.
 */
function ValueCountriesHelmet({ pinned, subject }) {
  const codes = (pinned && pinned.length ? pinned : ["GB", "US", "DE"]).join(",");
  const sub   = subject && subject !== "default" ? `&subject=${subject}` : "";
  const base  = process.env.REACT_APP_BACKEND_URL || "";
  const img   = `${base}/api/value/share.png?codes=${codes}${sub}`;
  const pageUrl = (typeof window !== "undefined")
    ? window.location.origin + window.location.pathname + window.location.search
    : "";
  const title = pinned?.length
    ? `${pinned.join(" vs ")} — Best-value study destinations`
    : "Where your money goes furthest · best-value study destinations";
  const desc  = "ROI, payback and home-fee eligibility across 22 destinations — see which country gives you the best value for an undergraduate degree.";
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={desc} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={desc} />
      <meta property="og:image" content={img} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:type" content="website" />
      {pageUrl && <meta property="og:url" content={pageUrl} />}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={img} />
    </Helmet>
  );
}

/**
 * ComparePanel — pin up to 3 countries and read a side-by-side money
 * story. Each column shows the five numbers that drive the ranking so
 * the user can see *why* one destination beats another, not just the
 * verdict. Rendered right under the league table.
 */
function ComparePanel({ rows, onUnpin, onClear, formatFrom }) {
  // Build the comparison rows up front so we can highlight the winner
  // in each metric. "winner" = best outcome (highest ROI, lowest cost…).
  const codes = rows.map((r) => r.code).join(",");
  const subject = rows[0]?.subject_key && rows[0].subject_key !== "default"
    ? `&subject=${rows[0].subject_key}` : "";
  // Hit the API through the same origin the frontend uses so the share
  // image loads over HTTPS and reuses the user's session cookie (which
  // lets the backend personalise home vs intl rates).
  const shareUrl = `${process.env.REACT_APP_BACKEND_URL}/api/value/share.png?codes=${codes}${subject}`;
  // Permalink that loads the exact same compare state when opened by
  // someone else. Lives on the frontend origin so Twitter / LinkedIn
  // unfurl respects it.
  const sub0 = rows[0]?.subject_key;
  const permaParts = [`pin=${codes}`];
  if (sub0 && sub0 !== "default") permaParts.push(`sub=${sub0}`);
  const permaUrl = `${window.location.origin}/value/countries?${permaParts.join("&")}`;
  const [copied, setCopied] = useState(false);
  const copyPerma = async () => {
    try {
      await navigator.clipboard.writeText(permaUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      // Fallback for insecure contexts
      window.prompt("Copy this link:", permaUrl);
    }
  };
  const metrics = [
    { key: "total_cost_gbp",    label: "Total degree cost",   direction: "low",  format: (v) => formatFrom(v, "GBP") },
    { key: "annual_fee_gbp",    label: "Tuition / yr",        direction: "low",  format: (v) => formatFrom(v, "GBP") },
    { key: "annual_living_gbp", label: "Living / yr",         direction: "low",  format: (v) => formatFrom(v, "GBP") },
    { key: "net_roi_10yr_gbp",  label: "10-yr net ROI",       direction: "high", format: (v) => formatFrom(v, "GBP") },
    { key: "payback_years",     label: "Simple payback",      direction: "low",  format: (v) => (v ? `${v} yrs` : "—") },
    { key: "value_score",       label: "Value score (0-100)", direction: "high", format: (v) => v?.toFixed?.(1) ?? v },
  ];
  const winnerIndex = (m) => {
    const nums = rows.map((r) => r[m.key]).map((v) => (typeof v === "number" ? v : null));
    if (nums.every((v) => v == null)) return -1;
    let best = m.direction === "high" ? -Infinity : Infinity;
    let idx = -1;
    nums.forEach((v, i) => {
      if (v == null) return;
      if (m.direction === "high" ? v > best : v < best) {
        best = v;
        idx = i;
      }
    });
    return idx;
  };

  return (
    <div
      className="mt-8 rounded-2xl border-2 border-amber-200 bg-amber-50/40 overflow-hidden"
      data-testid="compare-panel"
    >
      <div className="px-5 py-3 bg-amber-50 border-b border-amber-200 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Pin className="w-4 h-4 text-amber-600 fill-amber-400" />
          <div className="font-editorial text-lg text-stone-900">
            Side-by-side · 10-year money story
          </div>
          <div className="text-[11px] text-stone-500">({rows.length}/3)</div>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={copyPerma}
            data-testid="compare-copy-link"
            className="text-[12px] font-semibold text-[#4C1D95] hover:text-[#2E1065] inline-flex items-center gap-1"
          >
            {copied
              ? (<><Check className="w-3.5 h-3.5" /> Link copied</>)
              : (<><Link2 className="w-3.5 h-3.5" /> Copy link</>)}
          </button>
          <a
            href={shareUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="compare-share-png"
            className="text-[12px] font-semibold text-[#4C1D95] hover:text-[#2E1065] inline-flex items-center gap-1"
          >
            <Share2 className="w-3.5 h-3.5" /> Share this ranking
          </a>
          <button
            type="button"
            onClick={onClear}
            className="text-[12px] text-stone-600 hover:text-stone-900 inline-flex items-center gap-1"
            data-testid="compare-clear"
          >
            <X className="w-3 h-3" /> Clear
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="bg-amber-50/60">
              <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider text-stone-500 font-normal">Country</th>
              {rows.map((r) => (
                <th key={r.code} className="text-left px-4 py-2.5 min-w-[140px]">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xl">{r.flag}</span>
                      <span className="font-semibold text-stone-900">{r.name}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => onUnpin(r.code)}
                      className="text-stone-400 hover:text-stone-700"
                      aria-label={`Unpin ${r.name}`}
                      data-testid={`compare-unpin-${r.code}`}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="text-[11px] text-stone-500 mt-0.5">
                    {r.fee_status?.status === "home" ? "Home fee · " : "Intl fee · "}
                    {r.degree_length_yr} yr degree
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {metrics.map((m) => {
              const win = winnerIndex(m);
              return (
                <tr key={m.key} className="border-t border-amber-100" data-testid={`compare-metric-${m.key}`}>
                  <td className="px-4 py-2.5 text-stone-600 font-mono text-[12px]">{m.label}</td>
                  {rows.map((r, i) => (
                    <td
                      key={r.code + m.key}
                      className={`px-4 py-2.5 tabular-nums ${i === win ? "font-semibold text-emerald-700" : "text-stone-800"}`}
                    >
                      {m.format(r[m.key])}
                      {i === win && rows.length > 1 && (
                        <span className="ml-1 text-[9px] uppercase tracking-wider bg-emerald-100 text-emerald-700 px-1 py-0.5 rounded">Best</span>
                      )}
                    </td>
                  ))}
                </tr>
              );
            })}

            {/* Post-study work + scholarship summary — strings not
                numbers, so no best-of highlighting. */}
            <tr className="border-t border-amber-100">
              <td className="px-4 py-2.5 text-stone-600 font-mono text-[12px]">Post-study work</td>
              {rows.map((r) => (
                <td key={r.code + "psw"} className="px-4 py-2.5 text-stone-700 text-[12px]">
                  {r.post_study_work?.visa_name} · {r.post_study_work?.years} yr{r.post_study_work?.years === 1 ? "" : "s"}
                </td>
              ))}
            </tr>
            <tr className="border-t border-amber-100">
              <td className="px-4 py-2.5 text-stone-600 font-mono text-[12px]">Major scholarships</td>
              {rows.map((r) => (
                <td key={r.code + "sch"} className="px-4 py-2.5 text-stone-700 text-[12px]">
                  {r.scholarships?.major_count} schemes · avg {r.scholarships?.avg_coverage_pct}%
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
